import {useCallback, useEffect} from 'react';
import {useSelector} from 'react-redux';

import {
    selectCurrentUser,
    selectCurrentUserStatus,
    selectUserResources,
    selectUserResourcesStatus,
    selectUserTools,
    selectUserToolsStatus,
} from 'src/modules/main/services/user-info-slice';
import {getUserResources} from 'src/openapi/admin/apis/UserAccessApi';
import {getCurrentUser} from 'src/openapi/admin/apis/UserIdentityApi';
import {getUserTools} from 'src/openapi/admin/apis/UserToolsApi';
import {useAppDispatch} from 'src/store/hooks';

type UseCurrentUserOptions = {
    autoLoad?: boolean,
    enabled?: boolean,
};

let inflightLoad: Promise<void> | null = null;

export const useCurrentUser = (options: UseCurrentUserOptions = {}) => {
    const {autoLoad = false, enabled = true} = options;
    const apiDispatch = useAppDispatch();
    const currentUser = useSelector(selectCurrentUser);
    const currentUserStatus = useSelector(selectCurrentUserStatus);
    const currentUserResources = useSelector(selectUserResources);
    const resourcesStatus = useSelector(selectUserResourcesStatus);
    const userTools = useSelector(selectUserTools);
    const userToolsStatus = useSelector(selectUserToolsStatus);

    const loadCurrentUserInfo = useCallback(() => {
        if (!enabled) {
            return Promise.resolve();
        }

        if (currentUserStatus === 'loading' || currentUserStatus === 'succeeded' || currentUserStatus === 'unauthorized') {
            return inflightLoad ?? Promise.resolve();
        }

        if (inflightLoad) {
            return inflightLoad;
        }

        inflightLoad = (async () => {
            const response = await apiDispatch(getCurrentUser());
            if (response.payload?.error) {
                return;
            }

            const user = response.payload?.data;
            if (user && 'userId' in user && user.userId) {
                await Promise.all([
                    apiDispatch(getUserResources({userId: user.userId})),
                    apiDispatch(getUserTools({userId: user.userId})),
                ]);
            }
        })().finally(() => {
            inflightLoad = null;
        });

        return inflightLoad;
    }, [apiDispatch, currentUserStatus, enabled]);

    useEffect(() => {
        if (autoLoad && enabled && currentUserStatus === 'idle') {
            loadCurrentUserInfo();
        }
    }, [autoLoad, currentUserStatus, enabled, loadCurrentUserInfo]);

    const hasAccessToResource = useCallback((resource: string): boolean => {
        if (!currentUserResources) {
            return false;
        }
        return currentUserResources.includes(resource);
    }, [currentUserResources]);

    const isUnauthorized = currentUserStatus === 'unauthorized';
    const isDataLoaded = (
        currentUserStatus !== 'idle'
        && currentUserStatus !== 'loading'
        && resourcesStatus !== 'loading'
        && userToolsStatus !== 'loading'
    );

    return {
        loadCurrentUserInfo,
        currentUser,
        currentUserResources,
        currentUserStatus,
        resourcesStatus,
        userTools,
        userToolsStatus,
        isUnauthorized,
        isDataLoaded,
        hasAccessToResource,
    };
};
